/**
 * Что протухает, когда меняется правило.
 *
 * Явка не хранится посчитанной: сервер сводит её из событий и ДЕЙСТВУЮЩЕГО графика при
 * каждом запросе. Значит, изменив график отдела, мы не меняем ни одной сохранённой
 * цифры — мы меняем то, как посчитается следующий запрос. Ровно поэтому кэш надо
 * сбросить руками: иначе экран рядом с формой останется показывать опоздания,
 * посчитанные по правилу, которого уже нет, и это будет выглядеть как ошибка сервера.
 *
 * Список семейств живёт здесь, а не в форме, потому что отчёты явки читают из шести
 * разных мест, и «забыл одно» здесь означает один экран, который врёт до перезапуска.
 * Всякий новый экран с отчётом обязан дописать своё семейство сюда — первый сегмент
 * ключа и есть контракт.
 */
import type {QueryClient} from '@tanstack/react-query';

/**
 * Первые сегменты ключей всех запросов, ответ на которые зависит от графика.
 *
 * `control-departments` тоже здесь: проекция отдела несёт `attendance_schedule`, и
 * карточка отдела рисует график именно из неё.
 */
export const SCHEDULE_DEPENDENT_QUERIES = [
  'control-departments',
  'attendance-company',
  'attendance-department',
  'attendance-department-summary',
  'attendance-person',
  'personnel-attendance',
  'me-attendance',
  /** Ведомость делит начисленное на ПЛАНОВЫЕ дни — то есть на график. */
  'payroll',
] as const;

/**
 * Сбросить всё, что пересчитается по новому графику.
 *
 * Широко и намеренно: сузить до одного отдела нельзя — общий отчёт компании и личные
 * отчёты его людей меняются тоже, а по ключу отдел в них не виден.
 */
export function invalidateScheduleDependent(qc: QueryClient): Promise<void[]> {
  return Promise.all(
    SCHEDULE_DEPENDENT_QUERIES.map(key =>
      qc.invalidateQueries({queryKey: [key]}),
    ),
  );
}

/**
 * Первые сегменты ключей, зависящих от ОБЪЯВЛЕННЫХ СТАВОК.
 *
 * Явка от ставки не зависит, а ведомость зависит от обеих — поэтому список короче и
 * пересекается с верхним ровно в одной строке.
 */
export const SALARY_DEPENDENT_QUERIES = ['salary', 'payroll'] as const;

/** Сбросить историю ставок, действующие ставки и ведомость. */
export function invalidateSalaryDependent(qc: QueryClient): Promise<void[]> {
  return Promise.all(
    SALARY_DEPENDENT_QUERIES.map(key => qc.invalidateQueries({queryKey: [key]})),
  );
}
